import React, { useState, useRef } from 'react';
import { motion, useScroll, useTransform, useMotionValueEvent } from 'framer-motion';
import { Compass, PenTool, Hammer, Key } from 'lucide-react';
const steps = [{
  id: 1,
  phase: '01',
  title: 'Vision',
  subtitle: '& Discovery',
  description: 'We walk your land together, understanding its character—the way light falls through the pines, where the views open up, how the seasons will shape your daily life.',
  icon: Compass,
  image: 'https://cdn.magicpatterns.com/uploads/eGjGBfCJcdgjCDnaEG1bBN/image.png',
  imageAlt: 'Snow-covered North Idaho mountain vista with valley and forest views'
}, {
  id: 2,
  phase: '02',
  title: 'Design',
  subtitle: '& Architecture',
  description: 'Our architects translate vision into form—heavy timber frames that honor the forest, stone walls that echo the mountains, glass that frames the wilderness.',
  icon: PenTool,
  image: 'https://cdn.magicpatterns.com/uploads/pb66zdKvbpYgddcRNjDpjE/404-Mar_25_2026_01_12pm-oVHJ.jpg',
  imageAlt: 'Custom master bath with floating walnut vanity, vessel sinks, and organic-shaped mirrors'
}, {
  id: 3,
  phase: '03',
  title: 'Build',
  subtitle: '& Craftsmanship',
  description: 'Through snow and sun, our craftsmen work with precision. Foundations set in granite, frames raised against mountain backdrops—every detail executed with care.',
  icon: Hammer,
  image: 'https://cdn.magicpatterns.com/uploads/gtBwZ7iQhF5XW5fmCVpTY9/373-Mar_18_2026_02_15am-ipyj.jpg',
  imageAlt: 'Interior construction progress with bar area, timber columns, and arched alcove'
}, {
  id: 4,
  phase: '04',
  title: 'Handover',
  subtitle: '& Legacy',
  description: 'The moment arrives—floor-to-ceiling windows frame your view, the fireplace crackles, and your sanctuary in the wild becomes home.',
  icon: Key,
  image: 'https://cdn.magicpatterns.com/uploads/7TkYtmw8oYTrkB7RUJRvpL/0N8A8794.jpg',
  imageAlt: 'Vaulted great room with cathedral windows, cedar ceiling accents, and panoramic mountain views'
}];
export function ProcessTimeline() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeStep, setActiveStep] = useState(0);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end']
  });
  useMotionValueEvent(scrollYProgress, 'change', latest => {
    const stepIndex = Math.min(Math.floor(latest * steps.length), steps.length - 1);
    setActiveStep(stepIndex);
  });
  const progressWidth = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);
  return <section ref={containerRef} className="relative bg-[#1A1A1A]" style={{
    height: `${steps.length * 100}vh`
  }} data-id="element-278">
      <div className="sticky top-0 h-screen overflow-hidden" data-id="element-279">
        <div className="h-full flex" data-id="element-280">
          {/* Left Side - Sticky Text Content */}
          <div className="w-full lg:w-1/2 h-full flex flex-col justify-center px-8 md:px-16 lg:px-20 relative z-10" data-id="element-281">
            <div className="mb-12" data-id="element-282">
              <motion.span initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} className="text-[#A07656] uppercase tracking-[0.3em] text-xs font-semibold" data-id="element-283">
                The Process
              </motion.span>
            </div>

            <div className="relative min-h-[300px]" data-id="element-284">
              {steps.map((step, index) => <motion.div key={step.id} initial={false} animate={{
              opacity: activeStep === index ? 1 : 0,
              y: activeStep === index ? 0 : 20,
              pointerEvents: activeStep === index ? 'auto' : 'none'
            }} transition={{
              duration: 0.5,
              ease: [0.25, 0.1, 0.25, 1]
            }} className="absolute inset-0" data-id="element-285">
                  <div className="flex items-baseline gap-4 mb-6" data-id="element-286">
                    <span className="text-6xl md:text-7xl lg:text-8xl font-serif font-bold text-[#A07656]/20" data-id="element-287">
                      {step.phase}
                    </span>
                  </div>

                  <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-[#F5F3F0] leading-[1.1] mb-2" data-id="element-288">
                    {step.title}
                  </h2>
                  <h3 className="text-2xl md:text-3xl lg:text-4xl font-serif italic font-medium text-[#D4CFC8]/70 mb-8" data-id="element-289">
                    {step.subtitle}
                  </h3>

                  <p className="text-lg text-[#D4CFC8]/60 leading-relaxed max-w-md" data-id="element-290">
                    {step.description}
                  </p>

                  <div className="mt-10 w-14 h-14 border-2 border-[#A07656]/40 flex items-center justify-center" data-id="element-291">
                    <step.icon className="w-6 h-6 text-[#A07656]" data-id="element-292" />
                  </div>
                </motion.div>)}
            </div>

            {/* Progress indicator */}
            <div className="absolute bottom-12 left-8 md:left-16 lg:left-20 right-8 md:right-16 lg:right-20" data-id="element-293">
              <div className="flex items-center gap-3 mb-4" data-id="element-294">
                {steps.map((step, index) => <div key={step.id} className={`w-2 h-2 rounded-full transition-all duration-300 ${index === activeStep ? 'bg-[#A07656] scale-125' : index < activeStep ? 'bg-[#A07656]/50' : 'bg-[#F5F3F0]/20'}`} data-id="element-295" />)}
                <span className="ml-3 text-xs text-[#F5F3F0]/40 uppercase tracking-widest font-medium" data-id="element-296">
                  {activeStep + 1} of {steps.length}
                </span>
              </div>

              <div className="h-[2px] bg-[#F5F3F0]/10 w-full" data-id="element-297">
                <motion.div className="h-full bg-[#A07656]" style={{
                width: progressWidth
              }} data-id="element-298" />
              </div>
            </div>
          </div>

          {/* Right Side - Images */}
          <div className="hidden lg:block w-1/2 h-full relative" data-id="element-299">
            <div className="absolute inset-0" data-id="element-300">
              {steps.map((step, index) => <motion.div key={step.id} initial={false} animate={{
              opacity: activeStep === index ? 1 : 0,
              scale: activeStep === index ? 1 : 1.05
            }} transition={{
              duration: 0.7,
              ease: [0.25, 0.1, 0.25, 1]
            }} className="absolute inset-0" data-id="element-301">
                  <img src={step.image} alt={step.imageAlt} className="w-full h-full object-cover" data-id="element-302" />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/50 via-transparent to-transparent" data-id="element-303" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/40 via-transparent to-[#1A1A1A]/30" data-id="element-304" />
                </motion.div>)}
            </div>

            <div className="absolute top-12 right-12 bottom-12 left-0 border-t-2 border-r-2 border-b-2 border-[#A07656]/20 pointer-events-none" data-id="element-305" />
          </div>

          {/* Mobile background */}
          <div className="lg:hidden absolute inset-0 z-0" data-id="element-306">
            {steps.map((step, index) => <motion.div key={step.id} initial={false} animate={{
            opacity: activeStep === index ? 0.3 : 0
          }} transition={{
            duration: 0.5
          }} className="absolute inset-0" data-id="element-307">
                <img src={step.image} alt={step.imageAlt} className="w-full h-full object-cover" data-id="element-308" />
              </motion.div>)}
            <div className="absolute inset-0 bg-[#1A1A1A]/85" data-id="element-309" />
          </div>
        </div>
      </div>
    </section>;
}